import pb, { requireAuth } from '../pocketbaseClient';
import { collectBrowserMetadata } from '../utils/browserMetadata.js';
import { generateAccessToken } from '../utils/accessToken.js';
import { normalizeRecommendationTiers } from '../utils/scoreConfig.js';
import { migrateNotes, latestNoteText } from '../utils/notesFeed';
import { getJobTitleEntries } from '../utils/jobTitleExtraction';
import { getQuestionsFromQuestionnaireData } from '../utils/questionnairePages';

// Database Configuration - Später durch PocketBase-URL und API-Keys ersetzt
export const DB_CONFIG = {
    type: 'pocketbase', // Changed from 'localStorage' to 'pocketbase'
    apiKey: '', // Für später: PocketBase API Key
};

// Spalten, die auf einer Instanz fehlen koennen, weil sie per Skript oder im
// Admin nachgelegt werden. Scheitert das Anlegen einer Bewerbung, werden sie
// weggelassen und es wird noch einmal versucht — eine fehlende Zusatzspalte
// darf nie die Antworten kosten.
const OPTIONAL_SUBMISSION_COLUMNS = ['detailed_answers', 'job_title'];

// Resolves a user relation (created_by / assigned_to) into a lightweight
// { id, name, email } object, preferring an expanded record when available.
// Returns null when the relation is empty or the related user no longer exists.
export function mapRelationUser(id, expanded) {
    if (expanded && expanded.id) {
        return {
            id: expanded.id,
            name: expanded.name || '',
            username: expanded.username || '',
            email: expanded.email || ''
        };
    }
    if (id) {
        return { id, name: '', username: '', email: '' };
    }
    return null;
}

// Display name for a user object: name → username → email → fallback.
export function userDisplayName(user, fallback = '—') {
    if (!user) return fallback;
    return user.name || user.username || user.email || fallback;
}

// Database Interface - wird später durch PocketBase SDK ersetzt
export class DatabaseService {
    constructor() {
        this.isOnline = navigator.onLine;
        window.addEventListener('online', () => this.isOnline = true);
        window.addEventListener('offline', () => this.isOnline = false);
        
        // Initialize PocketBase if configured
        if (DB_CONFIG.type === 'pocketbase') {
            this.pb = pb;
        }
    }

    // Liest die Daten aus dem localStorage oder erstellt eine neue, leere Datenbank.
    async getDatabase() {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                // Ensure user is authenticated
                requireAuth();
                
                // For PocketBase, we'll return a hybrid structure
                const questionnaires = await this.getQuestionnaires();
                const submissions = await this.getSubmissions();
                const templates = await this.getTemplates();
                const settings = await this.getSettings();
                
                return {
                    questionnaires: questionnaires.reduce((acc, q) => {
                        acc[q.id] = q;
                        return acc;
                    }, {}),
                    submissions: submissions.reduce((acc, s) => {
                        acc[s.id] = s;
                        return acc;
                    }, {}),
                    templates: templates.reduce((acc, t) => {
                        acc[t.id] = t;
                        return acc;
                    }, {}),
                    settings
                };
            } catch (error) {
                console.error('PocketBase error:', error);
                throw error;
            }
        }
        
        // No localStorage fallback - PocketBase only
        throw new Error('PocketBase is required');
    }

    // Speichert die Datenbank
    async saveDatabase(data) {
        // No longer needed - PocketBase handles all persistence
        return;
    }

    // === QUESTIONNAIRE METHODS ===
    
    // Get all questionnaires from PocketBase
    async getQuestionnaires() {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                // Ensure user is authenticated
                requireAuth();
                
                let records;
                try {
                    records = await pb.collection('questionnaires').getFullList({
                        sort: '-created',
                        expand: 'created_by,assigned_to'
                    });
                } catch (expandError) {
                    // Fallback if created_by/assigned_to columns don't exist yet
                    console.warn('Questionnaire expand failed, retrying without creator/manager:', expandError);
                    records = await pb.collection('questionnaires').getFullList({
                        sort: '-created'
                    });
                }
                return records.map(record => ({
                    id: record.id,
                    title: record.title,
                    questions: typeof record.questions === 'string' ? JSON.parse(record.questions) : (record.questions || []),
                    defaultQuestions: record.defaultQuestions ? (typeof record.defaultQuestions === 'string' ? JSON.parse(record.defaultQuestions) : record.defaultQuestions) : [],
                    introText: record.introText || '',
                    outroText: record.outroText || '',
                    introWelcomeText: record.introWelcomeText || '',
                    introSectionsText: record.introSectionsText || '',
                    introTimerText: record.introTimerText || '',
                    introStartButtonText: record.introStartButtonText || '',
                    cancelText: record.cancelText || '',
                    cancelOutroText: record.cancelOutroText || '',
                    intro_page: record.intro_page ? (typeof record.intro_page === 'string' ? JSON.parse(record.intro_page) : record.intro_page) : null,
                    allowMultipleSubmissions: record.allow_multiple_submissions !== undefined
                        ? Boolean(record.allow_multiple_submissions)
                        : false,
                    scoring_config: record.scoring_config ? (typeof record.scoring_config === 'string' ? JSON.parse(record.scoring_config) : record.scoring_config) : null,
                    createdBy: mapRelationUser(record.created_by, record.expand?.created_by),
                    assignedTo: mapRelationUser(record.assigned_to, record.expand?.assigned_to),
                    created: record.created,
                    updated: record.updated
                }));
            } catch (error) {
                console.error('Error fetching questionnaires from PocketBase:', error);
                throw error;
            }
        }

        throw new Error('PocketBase is required');
    }

    // Lightweight questionnaire fetch for list/overview screens: only the fields
    // the results list and dashboard counter need (title + creator/manager), not
    // the heavy questions/intro_page/scoring_config blobs. Cuts a multi-MB payload
    // down to a few KB. Use getQuestionnaires() when the full record is required.
    async getQuestionnairesForList() {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();

                const expandUserFields = (rel) =>
                    `expand.${rel}.id,expand.${rel}.name,expand.${rel}.username,expand.${rel}.email`;
                // Bewusst ohne questions/defaultQuestions: die trugen bei ueber 500
                // Fragebogen zweistellige MB und wurden nur gebraucht, um die
                // Stelle je Bewerbung zu finden. Die steht jetzt in
                // submissions.job_title. Wer die Fragen braucht, nimmt
                // getQuestionnaire().
                const baseFields = 'id,title,template_name,created_by,assigned_to';

                let records;
                try {
                    records = await pb.collection('questionnaires').getFullList({
                        sort: '-created',
                        expand: 'created_by,assigned_to',
                        fields: `${baseFields},${expandUserFields('created_by')},${expandUserFields('assigned_to')}`
                    });
                } catch (expandError) {
                    // Fallback if created_by/assigned_to columns don't exist yet
                    console.warn('Questionnaire list expand failed, retrying without creator/manager:', expandError);
                    records = await pb.collection('questionnaires').getFullList({
                        sort: '-created',
                        fields: 'id,title,template_name'
                    });
                }

                return records.map(record => ({
                    id: record.id,
                    title: record.title,
                    templateName: record.template_name || null,
                    createdBy: mapRelationUser(record.created_by, record.expand?.created_by),
                    assignedTo: mapRelationUser(record.assigned_to, record.expand?.assigned_to)
                }));
            } catch (error) {
                console.error('Error fetching questionnaire list from PocketBase:', error);
                throw error;
            }
        }

        throw new Error('PocketBase is required');
    }

    // Get a single questionnaire by ID
    async getQuestionnaire(id) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                // For public applicant forms, we don't require authentication
                // The questionnaires collection should be configured to allow public access
                let record;
                try {
                    record = await pb.collection('questionnaires').getOne(id, {
                        expand: 'created_by,assigned_to'
                    });
                } catch (expandError) {
                    // Fallback if created_by/assigned_to columns don't exist yet
                    console.warn('Questionnaire expand failed, retrying without creator/manager:', expandError);
                    record = await pb.collection('questionnaires').getOne(id);
                }
                return {
                    id: record.id,
                    title: record.title,
                    templateName: record.template_name || null,
                    questions: typeof record.questions === 'string' ? JSON.parse(record.questions) : (record.questions || []),
                    defaultQuestions: record.defaultQuestions ? (typeof record.defaultQuestions === 'string' ? JSON.parse(record.defaultQuestions) : record.defaultQuestions) : [],
                    introText: record.introText || '',
                    outroText: record.outroText || '',
                    introWelcomeText: record.introWelcomeText || '',
                    introSectionsText: record.introSectionsText || '',
                    introTimerText: record.introTimerText || '',
                    introStartButtonText: record.introStartButtonText || '',
                    cancelText: record.cancelText || '',
                    cancelOutroText: record.cancelOutroText || '',
                    intro_page: record.intro_page ? (typeof record.intro_page === 'string' ? JSON.parse(record.intro_page) : record.intro_page) : null,
                    allowMultipleSubmissions: record.allow_multiple_submissions !== undefined
                        ? Boolean(record.allow_multiple_submissions)
                        : false,
                    scoring_config: record.scoring_config ? (typeof record.scoring_config === 'string' ? JSON.parse(record.scoring_config) : record.scoring_config) : null,
                    createdBy: mapRelationUser(record.created_by, record.expand?.created_by),
                    assignedTo: mapRelationUser(record.assigned_to, record.expand?.assigned_to),
                    created: record.created,
                    updated: record.updated
                };
            } catch (error) {
                console.error('Error fetching questionnaire from PocketBase:', error);
                throw error;
            }
        }
        
        throw new Error('PocketBase is required');
    }

    // Erstellt ein neues Questionnaire
    async createQuestionnaire(questionnaire) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                // Ensure user is authenticated
                requireAuth();
                
                // Ensure all required fields are present and properly formatted
                console.log('DatabaseService: Creating questionnaire with data:', {
                    title: questionnaire.title,
                    defaultQuestions: questionnaire.defaultQuestions,
                    defaultQuestionsType: typeof questionnaire.defaultQuestions,
                    defaultQuestionsLength: questionnaire.defaultQuestions ? questionnaire.defaultQuestions.length : 'N/A'
                });
                
                const data = {
                    title: questionnaire.title || '',
                    questions: JSON.stringify(questionnaire.questions || []),
                    defaultQuestions: JSON.stringify(questionnaire.defaultQuestions || []),
                    introText: questionnaire.introText || '',
                    outroText: questionnaire.outroText || '',
                    introWelcomeText: questionnaire.introWelcomeText || '',
                    introSectionsText: questionnaire.introSectionsText || '',
                    introTimerText: questionnaire.introTimerText || '',
                    introStartButtonText: questionnaire.introStartButtonText || '',
                    cancelText: questionnaire.cancelText || '',
                    cancelOutroText: questionnaire.cancelOutroText || '',
                    intro_page: questionnaire.intro_page ? JSON.stringify(questionnaire.intro_page) : null,
                    allow_multiple_submissions: Boolean(questionnaire.allowMultipleSubmissions),
                    scoring_config: questionnaire.scoring_config || null
                };

                // Stamp creator (immutable). The manager ("Zuständig") is
                // inherited from the editor/template selection — store it as-is
                // (null = Niemand) so the UI value and the saved value match.
                const currentUserId = pb.authStore.record?.id;
                if (currentUserId) {
                    data.created_by = currentUserId;
                }
                data.assigned_to = questionnaire.assignedTo || null;

                console.log('DatabaseService: Final data being sent to PocketBase:', {
                    title: data.title,
                    defaultQuestions: data.defaultQuestions,
                    questions: data.questions
                });
                
                // Validate required fields
                if (!data.title || data.title.trim() === '') {
                    throw new Error('Questionnaire title is required');
                }
                console.log('Sending questionnaire data to PocketBase:', data);
                const record = await pb.collection('questionnaires').create(data);
                console.log('Questionnaire created successfully:', record);
                return record.id;
            } catch (error) {
                console.error('Error creating questionnaire in PocketBase:', error);
                console.error('Error details:', {
                    message: error.message,
                    status: error.status,
                    data: error.data,
                    response: error.response
                });
                throw error;
            }
        }
        
        throw new Error('PocketBase is required');
    }

    // Update an existing questionnaire
    async updateQuestionnaire(id, questionnaire) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                // Ensure user is authenticated
                requireAuth();
                
                const data = {
                    title: questionnaire.title,
                    questions: questionnaire.questions,
                    defaultQuestions: questionnaire.defaultQuestions ? JSON.stringify(questionnaire.defaultQuestions) : null,
                    introText: questionnaire.introText || '',
                    outroText: questionnaire.outroText || '',
                    introWelcomeText: questionnaire.introWelcomeText || '',
                    introSectionsText: questionnaire.introSectionsText || '',
                    introTimerText: questionnaire.introTimerText || '',
                    introStartButtonText: questionnaire.introStartButtonText || '',
                    cancelText: questionnaire.cancelText || '',
                    cancelOutroText: questionnaire.cancelOutroText || '',
                    intro_page: questionnaire.intro_page ? JSON.stringify(questionnaire.intro_page) : null,
                    allow_multiple_submissions: Boolean(questionnaire.allowMultipleSubmissions),
                    scoring_config: questionnaire.scoring_config || null
                };

                // Allow reassigning the manager; never touch created_by here.
                if (questionnaire.assignedTo !== undefined) {
                    data.assigned_to = questionnaire.assignedTo || null;
                }

                const record = await pb.collection('questionnaires').update(id, data);
                
                return record.id;
            } catch (error) {
                console.error('Error updating questionnaire in PocketBase:', error);
                throw error;
            }
        }
        
        throw new Error('PocketBase is required');
    }

    // Reassign the internal "manager" of a questionnaire without touching any
    // of its other fields. Pass an empty string / null to unassign.
    async updateQuestionnaireManager(id, userId) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                const record = await pb.collection('questionnaires').update(id, {
                    assigned_to: userId || null
                }, { expand: 'created_by,assigned_to' });
                return {
                    success: true,
                    assignedTo: mapRelationUser(record.assigned_to, record.expand?.assigned_to)
                };
            } catch (error) {
                console.error('Error updating questionnaire manager in PocketBase:', error);
                return { success: false, error: error.message };
            }
        }

        throw new Error('PocketBase is required');
    }

    // Delete a questionnaire
    async deleteQuestionnaire(id) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                // Ensure user is authenticated
                requireAuth();
                
                await pb.collection('questionnaires').delete(id);
                
                return true;
            } catch (error) {
                console.error('Error deleting questionnaire from PocketBase:', error);
                throw error;
            }
        }
        
        throw new Error('PocketBase is required');
    }

    // === USER METHODS ===

    // Get all internal accounts (for creator/manager assignment pickers).
    // Returns a minimal list; requires the `users` collection to allow
    // authenticated list/view access.
    async getUsers() {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                // No `fields`/`sort` whitelist: PocketBase throws if a referenced
                // column (e.g. `name`) doesn't exist on this users collection.
                const records = await pb.collection('users').getFullList();
                const users = records.map(record => ({
                    id: record.id,
                    name: record.name || record.username || '',
                    email: record.email || ''
                }));
                users.sort((a, b) =>
                    (a.name || a.email).localeCompare(b.name || b.email)
                );
                return users;
            } catch (error) {
                // Most likely the `users` collection List/View rule blocks
                // authenticated listing — surface it loudly for debugging.
                console.error('Error fetching users from PocketBase (check the users collection List rule):', error);
                return [];
            }
        }

        throw new Error('PocketBase is required');
    }

    // === SUBMISSION METHODS ===

    // Erstellt eine neue Submission
    // Leitet die Stelle (Matrix-/Combobox-Antwort) aus den Antworten ab und gibt
    // sie als Anzeigetext zurueck, wie ihn die Uebersicht frueher selbst gebaut
    // hat. Die Fragen kommen vom Aufrufer, wenn er sie ohnehin geladen hat —
    // sonst werden sie hier nachgeholt. getQuestionnaire braucht keine Auth, der
    // oeffentliche Bewerberbogen kann die Ableitung also ebenfalls ausfuehren.
    async deriveJobTitle(submission) {
        let questions = submission.questions;

        if (!Array.isArray(questions)) {
            if (!submission.questionnaireId) return '';
            const questionnaire = await this.getQuestionnaire(submission.questionnaireId);
            // Derselbe Weg wie frueher in der Uebersicht: Standardfragen zuerst,
            // dann die abgeflachten Seitenfragen. Ein Fragebogen kann seine
            // Fragen in questionPages statt in questions halten — direkt
            // .questions zu lesen faende dort nichts.
            questions = [
                ...(questionnaire?.defaultQuestions || []),
                ...getQuestionsFromQuestionnaireData(questionnaire || {})
            ];
        }

        return getJobTitleEntries(questions, submission.answers || {}).join(', ');
    }

    async createSubmission(submission) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                // Collect browser metadata
                const browserMetadata = collectBrowserMetadata();
                
                const data = {
                    questionnaire_id: submission.questionnaireId,
                    questionnaire: submission.questionnaire || submission.questionnaireId || null,
                    access_token: submission.accessToken || null,
                    applicant_name: submission.applicantName,
                    answers: JSON.stringify(submission.answers || {}),
                    completion_summary: submission.completionSummary ? JSON.stringify(submission.completionSummary) : null,
                    completion_duration: submission.completionDuration || null,
                    submitted_at: new Date().toISOString(),
                    meta_data: JSON.stringify(browserMetadata || {}),
                    answer_log: submission.answerLog ? JSON.stringify(submission.answerLog) : null,
                    // Der selbsttragende Abzug: zu jeder Frage ihr Text, ihr Typ,
                    // ihre Optionen und die Antwort. `answers` allein ist nach
                    // Frage-ID geschluesselt und damit nur so lange lesbar, wie
                    // der Fragebogen diese IDs noch kennt — ein neu geladenes
                    // Template oder ein JSON-Import vergibt neue und laesst die
                    // Antworten verwaisen. Dieser Abzug ueberlebt das.
                    detailed_answers: submission.detailedAnswers ? JSON.stringify(submission.detailedAnswers) : null,
                    status: submission.status || 'Unbearbeitet',
                    submission_type: submission.submissionType || 'normal'
                };

                // Die Stelle wird hier einmal festgehalten, statt sie später in
                // der Übersicht aus allen Fragen und allen Antworten neu zu
                // berechnen — das kostete dort zweistellige MB pro Seitenaufruf.
                // Ein Fehler darf eine Bewerbung nie verhindern: schlaegt die
                // Herleitung fehl, bleibt das Feld leer.
                try {
                    data.job_title = await this.deriveJobTitle(submission);
                } catch (jobTitleError) {
                    console.warn('Job title derivation failed, storing none:', jobTitleError);
                }
                
                // Include notes in analysis if provided
                if (submission.notes) {
                    data.analysis = JSON.stringify({
                        notes: submission.notes
                    });
                }
                
                // For public applicant forms, we don't require authentication
                // The submissions collection should be configured to allow public access
                // detailed_answers und job_title werden per Skript bzw. im Admin
                // angelegt. Fehlt eine Spalte auf dieser Instanz, lehnt
                // PocketBase den ganzen Datensatz ab — das darf keine Bewerbung
                // kosten. Also einmal vollstaendig versuchen und dann Spalte fuer
                // Spalte weglassen, statt gleich alle: lieber nur den Abzug
                // verlieren als zusaetzlich die Stelle.
                const attempts = [data];
                const reduced = { ...data };
                for (const column of OPTIONAL_SUBMISSION_COLUMNS) {
                    if (!(column in reduced)) continue;
                    delete reduced[column];
                    attempts.push({ ...reduced });
                }

                let record = null;
                let lastError = null;
                for (const [index, attempt] of attempts.entries()) {
                    try {
                        record = await pb.collection('submissions').create(attempt);
                        if (index > 0) {
                            const dropped = Object.keys(data).filter((key) => !(key in attempt));
                            console.warn(`Submission created without ${dropped.join(', ')} — Spalte(n) fehlen in PocketBase.`);
                        }
                        break;
                    } catch (createError) {
                        lastError = createError;
                    }
                }
                if (!record) throw lastError;

                return record.id;
            } catch (error) {
                console.error('Error creating submission in PocketBase:', error);
                console.error('Error details:', {
                    message: error.message,
                    status: error.status,
                    data: error.data,
                    response: error.response
                });
                throw error;
            }
        }
        
        throw new Error('PocketBase is required');
    }

    async createQuestionnaireAccessToken(questionnaireId) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                const token = generateAccessToken();
                await pb.collection('questionnaire_access_tokens').create({
                    token,
                    questionnaire: questionnaireId,
                    questionnaire_id: questionnaireId,
                    status: 'active'
                });
                return token;
            } catch (error) {
                console.error('Error creating questionnaire access token:', error);
                throw error;
            }
        }

        throw new Error('PocketBase is required');
    }

    async getQuestionnaireAccessToken(token) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                if (!token) return null;
                const escapedToken = String(token).replace(/"/g, '\\"');
                const record = await pb.collection('questionnaire_access_tokens').getFirstListItem(
                    `token = "${escapedToken}"`
                );

                return {
                    id: record.id,
                    token: record.token,
                    questionnaireId: record.questionnaire || record.questionnaire_id || null,
                    status: record.status || 'active',
                    usedAt: record.used_at || null
                };
            } catch (error) {
                if (error?.status === 404) return null;
                console.error('Error loading questionnaire access token:', error);
                throw error;
            }
        }

        throw new Error('PocketBase is required');
    }

    async isAccessTokenAlreadyUsed(token) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                if (!token) return false;
                const escapedToken = String(token).replace(/"/g, '\\"');
                const result = await pb.collection('submissions').getList(1, 1, {
                    filter: `access_token = "${escapedToken}"`
                });
                return result.totalItems > 0;
            } catch (error) {
                // If we can't query submissions (e.g. no public list rule), fall back to token status check
                return false;
            }
        }
        return false;
    }

    async consumeQuestionnaireAccessToken(token) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                if (!token) return false;
                const tokenRecord = await this.getQuestionnaireAccessToken(token);
                if (!tokenRecord || tokenRecord.status !== 'active') return false;

                try {
                    await pb.collection('questionnaire_access_tokens').update(tokenRecord.id, {
                        status: 'used',
                        used_at: new Date().toISOString()
                    });
                } catch (updateError) {
                    console.warn('Could not update access token status:', updateError?.status, updateError?.message);
                    return false;
                }
                return true;
            } catch (error) {
                console.error('Error consuming questionnaire access token:', error);
                throw error;
            }
        }

        throw new Error('PocketBase is required');
    }

    // Maps a raw PocketBase submission record to our app shape. Heavy JSON
    // columns (answers, answer_log, completion_summary, meta_data) may be
    // absent when fetched via a trimmed field set — they fall back to defaults.
    mapSubmissionRecord(record) {
        return {
            id: record.id,
            questionnaireId: record.questionnaire_id,
            applicantName: record.applicant_name,
            // Beim Absenden abgeleitet und gespeichert; leer bei Altdatensaetzen,
            // die das Backfill noch nicht gesehen hat.
            jobTitle: record.job_title || '',
            answers: record.answers ? (typeof record.answers === 'string' ? JSON.parse(record.answers) : record.answers) : {},
            completionSummary: record.completion_summary ? (typeof record.completion_summary === 'string' ? JSON.parse(record.completion_summary) : record.completion_summary) : null,
            completionDuration: record.completion_duration || null,
            notes: latestNoteText(migrateNotes(record.analysis)),
            noteEntries: migrateNotes(record.analysis),
            submittedAt: new Date(record.submitted_at),
            analysis: record.analysis ? (typeof record.analysis === 'string' ? JSON.parse(record.analysis) : record.analysis) : null,
            qualification_evaluation: record.qualification_evaluation || null,
            point_evaluation: record.point_evaluation || null,
            status: record.status || 'Unbearbeitet',
            assignedTo: mapRelationUser(record.assigned_to, record.expand?.assigned_to),
            answerLog: record.answer_log ? (typeof record.answer_log === 'string' ? JSON.parse(record.answer_log) : record.answer_log) : null,
            detailedAnswers: record.detailed_answers ? (typeof record.detailed_answers === 'string' ? JSON.parse(record.detailed_answers) : record.detailed_answers) : null,
            metaData: record.meta_data ? (typeof record.meta_data === 'string' ? JSON.parse(record.meta_data) : record.meta_data) : null,
            accessToken: record.access_token || null
        };
    }

    // Holt alle Submissions (Admin only) — full records incl. heavy columns
    async getSubmissions() {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                // For admin access, ensure user is authenticated
                requireAuth();

                const records = await pb.collection('submissions').getFullList({
                    sort: '-submitted_at'
                });
                return records.map(record => this.mapSubmissionRecord(record));
            } catch (error) {
                console.error('Error fetching submissions from PocketBase:', error);
                throw error;
            }
        }

        throw new Error('PocketBase is required');
    }

    // Dashboard counters only need totals, not full records. Fetch just id +
    // analysis (skipping the heavy answers/evaluation blobs) and derive the
    // counts. `analyses` matches the previous `submissions.filter(s => s.analysis)`
    // truthiness exactly.
    async getSubmissionStats() {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();

                const records = await pb.collection('submissions').getFullList({
                    fields: 'id,analysis'
                });
                const hasAnalysis = (raw) => {
                    if (!raw) return false;
                    if (typeof raw === 'string') {
                        try { return Boolean(JSON.parse(raw)); } catch (e) { return false; }
                    }
                    return true;
                };
                return {
                    total: records.length,
                    analyses: records.filter(r => hasAnalysis(r.analysis)).length
                };
            } catch (error) {
                console.error('Error fetching submission stats from PocketBase:', error);
                throw error;
            }
        }

        throw new Error('PocketBase is required');
    }

    // Lightweight submission list for the results overview. Fetches only the
    // columns the list renders/sorts/filters by, omitting the heavy JSON
    // payloads (answers, answer_log, completion_summary, meta_data) which are
    // only needed when a single applicant's detail view is opened.
    async getSubmissionsForList() {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();

                // job_title haelt die Stelle als fertigen Text. Frueher stand hier
                // answers, damit die Uebersicht sie selbst aus der Matrixfrage
                // ziehen konnte — das lud die Antworten aller Bewerber und war
                // der zweitgroesste Posten der Seite.
                const baseFields = 'id,questionnaire_id,applicant_name,submitted_at,status,analysis,qualification_evaluation,point_evaluation,job_title';
                let records;
                try {
                    records = await pb.collection('submissions').getFullList({
                        sort: '-submitted_at',
                        expand: 'assigned_to',
                        fields: `${baseFields},assigned_to,expand.assigned_to.id,expand.assigned_to.name,expand.assigned_to.username,expand.assigned_to.email`
                    });
                } catch (expandError) {
                    // The submissions.assigned_to field may not exist yet (manual
                    // PocketBase admin step) — fall back to the plain list.
                    console.warn('Submission list expand failed, retrying without assignee:', expandError);
                    records = await pb.collection('submissions').getFullList({
                        sort: '-submitted_at',
                        fields: baseFields
                    });
                }
                return records.map(record => this.mapSubmissionRecord(record));
            } catch (error) {
                console.error('Error fetching submission list from PocketBase:', error);
                throw error;
            }
        }

        throw new Error('PocketBase is required');
    }

    // Holt die Antworten weniger, namentlich bekannter Bewerbungen nach.
    // Gebraucht vom Wiederbewerber-Abgleich: der Namenstreffer steht schon in
    // der Liste, bestaetigen laesst er sich aber nur ueber E-Mail/Telefon aus
    // `answers` — und genau dieses Feld laesst die Liste bewusst weg (siehe
    // docs/results-page-payload.md). Deshalb eine gefilterte Einzelabfrage
    // statt eines groesseren Listen-Payloads fuer alle.
    async getSubmissionAnswersByIds(ids = []) {
        if (DB_CONFIG.type === 'pocketbase') {
            const unique = [...new Set((ids || []).filter(Boolean))];
            if (unique.length === 0) return {};
            try {
                requireAuth();
                // Obergrenze, damit ein sehr haeufiger Name keine ueberlange
                // Filter-URL erzeugt. Mehr als ein paar Vorbewerbungen zeigt
                // die Oberflaeche ohnehin nicht an.
                const capped = unique.slice(0, 25);
                const filter = capped.map((id) => `id = "${String(id).replace(/"/g, '\\"')}"`).join(' || ');
                const records = await pb.collection('submissions').getFullList({
                    filter,
                    fields: 'id,answers'
                });
                const byId = {};
                records.forEach((record) => {
                    byId[record.id] = record.answers
                        ? (typeof record.answers === 'string' ? JSON.parse(record.answers) : record.answers)
                        : {};
                });
                return byId;
            } catch (error) {
                // Ein Fehlschlag darf die Detailansicht nie kosten: ohne
                // Antworten bleibt es beim Namenstreffer.
                console.warn('Error fetching submission answers by ids:', error);
                return {};
            }
        }

        throw new Error('PocketBase is required');
    }

    // Stats page needs completion_summary + completion_duration (which the
    // list fetch omits) plus scores/answers/status. One aggregate read.
    async getSubmissionsForStats() {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                const records = await pb.collection('submissions').getFullList({
                    fields: 'id,questionnaire_id,submitted_at,status,point_evaluation,qualification_evaluation,answers,completion_summary,completion_duration,access_token',
                    sort: '-submitted_at'
                });
                return records.map(record => this.mapSubmissionRecord(record));
            } catch (error) {
                console.error('Error fetching submissions for stats:', error);
                throw error;
            }
        }
        throw new Error('PocketBase is required');
    }

    // "Sent" source: each questionnaire_access_token = one generated
    // questionnaire link. Only the invitation flow — open campaign applications
    // create no token. The matching "submitted" count comes from the submission
    // records (access_token + submitted_at), NOT the token's used_at/status,
    // which is only written by the admin flow. Returns [] on error so the stats
    // page degrades to "—" instead of crashing.
    async getAccessTokensForStats() {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                const records = await pb.collection('questionnaire_access_tokens').getFullList({
                    fields: 'id,questionnaire_id,questionnaire,status,used_at,created',
                    sort: '-created'
                });
                return records.map(r => ({
                    id: r.id,
                    questionnaireId: r.questionnaire_id || r.questionnaire || null,
                    status: r.status || 'active',
                    createdAt: new Date(r.created),
                    usedAt: r.used_at ? new Date(r.used_at) : null
                }));
            } catch (error) {
                console.error('Error fetching access tokens for stats:', error);
                return [];
            }
        }
        throw new Error('PocketBase is required');
    }

    // Holt eine einzelne Submission inkl. aller Felder (Detailansicht)
    async getSubmission(id) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                let record;
                try {
                    record = await pb.collection('submissions').getOne(id, { expand: 'assigned_to' });
                } catch (expandError) {
                    console.warn('Submission expand failed, retrying without assignee:', expandError);
                    record = await pb.collection('submissions').getOne(id);
                }
                return this.mapSubmissionRecord(record);
            } catch (error) {
                console.error('Error fetching submission from PocketBase:', error);
                throw error;
            }
        }

        throw new Error('PocketBase is required');
    }

    // Reassign the internal account responsible ("Zuständig") for a single
    // submission. An empty userId clears the per-submission override so the
    // questionnaire's default applies again (single-submission questionnaires).
    async updateSubmissionAssignee(submissionId, userId) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                const record = await pb.collection('submissions').update(submissionId, {
                    assigned_to: userId || null
                }, { expand: 'assigned_to' });
                return {
                    success: true,
                    assignedTo: mapRelationUser(record.assigned_to, record.expand?.assigned_to)
                };
            } catch (error) {
                console.error('Error updating submission assignee in PocketBase:', error);
                return { success: false, error: error.message };
            }
        }

        throw new Error('PocketBase is required');
    }

    // Check if applicant has already submitted for a specific questionnaire
    // Note: submissions list requires auth — returns empty array for unauthenticated users
    async checkApplicantSubmission(questionnaireId) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                const records = await pb.collection('submissions').getFullList({
                    filter: `questionnaire_id = "${questionnaireId}"`,
                    sort: '-submitted_at'
                });

                // Return only basic info needed for duplicate check
                return records.map(record => ({
                    id: record.id,
                    questionnaireId: record.questionnaire_id,
                    applicantName: record.applicant_name,
                    submittedAt: new Date(record.submitted_at),
                    status: record.status || 'Unbearbeitet',
                    submissionType: record.submission_type || 'normal'
                }));
            } catch (error) {
                // If unauthorized, return empty array — duplicate check is non-critical
                if (error.status === 403 || error.status === 401) {
                    console.warn('Submissions list requires auth — skipping duplicate check');
                    return [];
                }
                console.error('Error checking applicant submission status:', error);
                throw error;
            }
        }

        throw new Error('PocketBase is required');
    }

    // Delete a submission
    async deleteSubmission(submissionId) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                await pb.collection('submissions').delete(submissionId);
                
                return true;
            } catch (error) {
                console.error('Error deleting submission from PocketBase:', error);
                throw error;
            }
        }
        
        throw new Error('PocketBase is required');
    }

    // Update a submission (for notes, analysis, etc.)
    async updateSubmission(submissionId, updates) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                const data = {};
                
                // Map frontend field names to PocketBase field names
                if (updates.notes !== undefined || updates.noteEntries !== undefined) {
                    // Fetch the current record once and merge both notes fields in a
                    // single write so that passing both in one call never discards either.
                    const currentRecord = await pb.collection('submissions').getOne(submissionId);
                    let currentAnalysis = currentRecord.analysis || {};
                    if (typeof currentAnalysis === 'string') {
                        try { currentAnalysis = JSON.parse(currentAnalysis); } catch (e) { currentAnalysis = {}; }
                    }
                    const mergedAnalysis = { ...currentAnalysis };
                    if (updates.notes !== undefined) {
                        mergedAnalysis.notes = updates.notes;
                    }
                    // noteEntries applied after notes so the derived value wins when both
                    // are present in the same call.
                    if (updates.noteEntries !== undefined) {
                        mergedAnalysis.noteEntries = updates.noteEntries;
                        mergedAnalysis.notes = latestNoteText(updates.noteEntries);
                    }
                    data.analysis = JSON.stringify(mergedAnalysis);
                }
                if (updates.analysis !== undefined) {
                    data.analysis = JSON.stringify(updates.analysis);
                }
                if (updates.answers !== undefined) {
                    data.answers = JSON.stringify(updates.answers);
                }
                if (updates.status !== undefined) {
                    data.status = updates.status;
                }
                if (updates.point_evaluation !== undefined) {
                    data.point_evaluation = JSON.stringify(updates.point_evaluation);
                }

                const record = await pb.collection('submissions').update(submissionId, data);
                
                // Parse the response fields
                const parsedAnalysis = record.analysis ? (typeof record.analysis === 'string' ? JSON.parse(record.analysis) : record.analysis) : null;
                const parsedAnswers = record.answers ? (typeof record.answers === 'string' ? JSON.parse(record.answers) : record.answers) : {};
                
                return {
                    id: record.id,
                    questionnaireId: record.questionnaire_id,
                    applicantName: record.applicant_name,
                    answers: parsedAnswers,
                    notes: parsedAnalysis?.notes || '',
                    submittedAt: new Date(record.submitted_at),
                    analysis: parsedAnalysis,
                    point_evaluation: record.point_evaluation ? (typeof record.point_evaluation === 'string' ? JSON.parse(record.point_evaluation) : record.point_evaluation) : null,
                    status: record.status || 'Unbearbeitet'
                };
            } catch (error) {
                console.error('Error updating submission in PocketBase:', error);
                console.error('Error details:', {
                    message: error.message,
                    status: error.status,
                    data: error.data,
                    response: error.response
                });
                throw error;
            }
        }
        
        throw new Error('PocketBase is required');
    }

    // === TEMPLATE METHODS ===
    
    // Holt alle Templates
    async getTemplates() {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                const records = await pb.collection('templates').getFullList({ sort: '-created' });
                return records.map(record => ({
                        id: record.id,
                        name: record.name,
                        title: record.title,
                        questions: record.questions ? (typeof record.questions === 'string' ? JSON.parse(record.questions) : record.questions) : [],
                        introText: record.introText || '',
                        outroText: record.outroText || '',
                    introWelcomeText: record.introWelcomeText || '',
                    introSectionsText: record.introSectionsText || '',
                    introTimerText: record.introTimerText || '',
                    introStartButtonText: record.introStartButtonText || '',
                        qualification_prompt: record.qualification_prompt || '',
                        analysis_prompt: record.analysis_prompt || '',
                        scoring_prompt: record.scoring_prompt || '',
                        cancelText: record.cancelText || '',
                        cancelOutroText: record.cancelOutroText || '',
                    intro_page: record.intro_page ? (typeof record.intro_page === 'string' ? JSON.parse(record.intro_page) : record.intro_page) : null,
                    defaultQuestions: record.defaultQuestions ? (typeof record.defaultQuestions === 'string' ? JSON.parse(record.defaultQuestions) : record.defaultQuestions) : null,
                    archived: !!record.archived,
                        created: record.created,
                        updated: record.updated
                }));
            } catch (error) {
                console.error('Error fetching templates from PocketBase:', error);
                throw error;
            }
        }
        
        throw new Error('PocketBase is required');
    }

    // Speichert ein Template
    async saveTemplate(template) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                const data = {
                    name: template.name,
                    title: template.title,
                    questions: JSON.stringify(template.questions || []),
                    introText: template.introText || '',
                    outroText: template.outroText || '',
                    introWelcomeText: template.introWelcomeText || '',
                    introSectionsText: template.introSectionsText || '',
                    introTimerText: template.introTimerText || '',
                    introStartButtonText: template.introStartButtonText || '',
                    qualification_prompt: template.qualification_prompt || '',
                    analysis_prompt: template.analysis_prompt || '',
                    scoring_prompt: template.scoring_prompt || '',
                    cancelText: template.cancelText || '',
                    cancelOutroText: template.cancelOutroText || '',
                    intro_page: template.intro_page ? JSON.stringify(template.intro_page) : null,
                    defaultQuestions: template.defaultQuestions ? JSON.stringify(template.defaultQuestions) : null,
                    archived: false
                };

                let record;
                if (template.id) {
                    record = await pb.collection('templates').update(template.id, data);
                } else {
                    record = await pb.collection('templates').create(data);
                }
                
                return record.id;
            } catch (error) {
                console.error('Error saving template in PocketBase:', error);
                console.error('Error details:', {
                    message: error.message,
                    status: error.status,
                    data: error.data,
                    response: error.response
                });
                throw error;
            }
        }
        
        throw new Error('PocketBase is required');
    }

    // Aktualisiert ein bestehendes Template
    async updateTemplate(templateId, template) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                const data = {
                    name: template.name,
                    title: template.title,
                    questions: JSON.stringify(template.questions || []),
                    introText: template.introText || '',
                    outroText: template.outroText || '',
                    introWelcomeText: template.introWelcomeText || '',
                    introSectionsText: template.introSectionsText || '',
                    introTimerText: template.introTimerText || '',
                    introStartButtonText: template.introStartButtonText || '',
                    qualification_prompt: template.qualification_prompt || '',
                    analysis_prompt: template.analysis_prompt || '',
                    scoring_prompt: template.scoring_prompt || '',
                    cancelText: template.cancelText || '',
                    cancelOutroText: template.cancelOutroText || '',
                    intro_page: template.intro_page ? JSON.stringify(template.intro_page) : null,
                    defaultQuestions: template.defaultQuestions ? JSON.stringify(template.defaultQuestions) : null,
                    archived: false
                };

                console.log('DatabaseService: Updating template with data:', {
                    templateId,
                    analysis_prompt: data.analysis_prompt,
                    qualification_prompt: data.qualification_prompt,
                    intro_page: data.intro_page,
                    defaultQuestions: data.defaultQuestions,
                    questionsLength: data.questions ? data.questions.length : 0
                });
                
                const record = await pb.collection('templates').update(templateId, data);
                
                return record.id;
            } catch (error) {
                console.error('Error updating template in PocketBase:', error);
                console.error('Error details:', {
                    message: error.message,
                    status: error.status,
                    data: error.data,
                    response: error.response
                });
                throw error;
            }
        }
        
        throw new Error('PocketBase is required');
    }

    // Renames a template in place (partial update of only the name field, so the
    // questions and all other content are left untouched).
    async renameTemplate(templateId, name) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                const record = await pb.collection('templates').update(templateId, { name: (name || '').trim() });
                return record.id;
            } catch (error) {
                console.error('Error renaming template in PocketBase:', error);
                throw error;
            }
        }

        throw new Error('PocketBase is required');
    }

    // Archiviert eine Vorlage oder stellt sie wieder her (partielles Update,
    // der restliche Inhalt bleibt unangetastet).
    async setTemplateArchived(templateId, archived) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                const record = await pb.collection('templates').update(templateId, { archived: !!archived });
                return record.id;
            } catch (error) {
                console.error('Error updating template archive state in PocketBase:', error);
                throw error;
            }
        }

        throw new Error('PocketBase is required');
    }

    // Löscht ein Template
    async deleteTemplate(templateId) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                await pb.collection('templates').delete(templateId);
                
                return true;
            } catch (error) {
                console.error('Error deleting template from PocketBase:', error);
                throw error;
            }
        }
        
        throw new Error('PocketBase is required');
    }

    // === SETTINGS METHODS ===
    
    // Holt Settings
    async getSettings() {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                const records = await pb.collection('settings').getFullList();
                if (records.length > 0) {
                    const settings = records[0];
                    return {
                        systemPrompt: settings.system_prompt || '',
                        questionnairePrompt: settings.questionnaire_prompt || '',
                        qualificationPrompt: settings.eligibility_prompt || '',
                        pointScoringPrompt: settings.point_scoring_prompt || '',
                        evaluationLanguageInstruction: settings.evaluationLanguageInstruction || settings.evaluation_language_instruction || '',
                        userColors: settings.user_colors
                            ? (typeof settings.user_colors === 'string' ? JSON.parse(settings.user_colors) : settings.user_colors)
                            : {},
                        recommendationTiers: normalizeRecommendationTiers(
                            typeof settings.recommendation_thresholds === 'string'
                                ? JSON.parse(settings.recommendation_thresholds || 'null')
                                : settings.recommendation_thresholds
                        )
                    };
                }
                // Return default settings if none exist
                return {
                    systemPrompt: "Du bewertest hochspezifische Bewerbungen für Positionen in Heidelberg, Deutschland (Altstadt). Fokussiere auf die SPEZIELLE BRANCHE/DOMÄNE, nicht nur allgemeine Fähigkeiten. Analysiere: Branchenspezifisches Fachwissen, Produktkenntnisse, lokale Marktkenntnis, Fachbegriffe und Konzepte, branchenspezifische Erfahrungen. Bewerte Klarheit, Relevanz und Problemlösungskompetenz. Berücksichtige, dass Fachkräfte Branchenterminologie als selbstverständlich betrachten.",
                    questionnairePrompt: "Du erstellst hochspezifische Fragebögen für Positionen in Heidelberg, Deutschland (Altstadt). Fokussiere auf die SPEZIELLE BRANCHE/DOMÄNE, nicht nur allgemeine Fähigkeiten. Erstelle Fragen zu: Branchenspezifisches Fachwissen, Produktkenntnisse, lokale Marktkenntnis, Fachbegriffe und Konzepte, branchenspezifische Erfahrungen. Stelle sicher, dass die Fragen klar, relevant und praxisorientiert sind. Berücksichtige, dass Fachkräfte Branchenterminologie als selbstverständlich betrachten. Erstelle eine ausgewogene Mischung aus 2-3 Textfragen, 1-2 Einzelauswahlfragen und 1-2 Mehrfachauswahlfragen. MINDESTENS 1 kreative Persönlichkeits-Textfrage einbauen. Wichtige Informationen in den Fragen selbst integrieren, nicht in Beschreibungen. Geschäftsbegriffe als selbstverständlich voraussetzen. Beschreibungen nur bei Bedarf und kurz halten.",
                    qualificationPrompt: "Bewerte die Bewerbungsantworten auf einer Skala von 0-10 Punkten nach unseren Kriterien.\n\nBewerte basierend auf: Abschluss, Erfahrung, Position, Starttermin, Schichtarbeit, Nebentätigkeit, Ausfälle, Anfahrtsweg, Verbleib und Termine.\n\nAntworte NUR mit einem JSON-Objekt im Format:\n{\n  \"score\": [Zahl von 0-10],\n  \"explanation\": \"[Kurze Begründung der Gesamtbewertung]\",\n  \"questionEvaluations\": [\n    {\n      \"questionId\": \"default_1\",\n      \"score\": 2,\n      \"maxScore\": 2,\n      \"passed\": true,\n      \"reason\": \"Gute Qualifikation\"\n    }\n  ]\n}",
                    pointScoringPrompt: '',
                    evaluationLanguageInstruction: '',
                    userColors: {},
                    recommendationTiers: normalizeRecommendationTiers(null)
                };
            } catch (error) {
                console.error('Error fetching settings from PocketBase:', error);
                throw error;
            }
        }
        
        throw new Error('PocketBase is required');
    }

    // Speichert Settings
    async saveSettings(settings) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                const data = {
                    system_prompt: settings.systemPrompt || '',
                    questionnaire_prompt: settings.questionnairePrompt || '',
                    eligibility_prompt: settings.qualificationPrompt || '',
                    point_scoring_prompt: settings.pointScoringPrompt || '',
                    evaluationLanguageInstruction: settings.evaluationLanguageInstruction || '',
                    recommendation_thresholds: JSON.stringify(
                        normalizeRecommendationTiers(settings.recommendationTiers)
                    )
                };

                // Check if settings already exist
                const existingSettings = await pb.collection('settings').getFullList();

                const writeSettings = async (payload) => {
                    if (existingSettings.length > 0) {
                        return pb.collection('settings').update(existingSettings[0].id, payload);
                    }
                    return pb.collection('settings').create(payload);
                };

                // Columns that may not exist yet in the PocketBase settings
                // schema; dropped one by one if the write is rejected.
                const optionalColumns = ['point_scoring_prompt', 'recommendation_thresholds'];
                let record;
                let payload = { ...data };
                for (;;) {
                    try {
                        record = await writeSettings(payload);
                        break;
                    } catch (writeError) {
                        // Gracefully degrade if PocketBase rejects an unknown field
                        // (e.g. column not yet added to the schema).
                        const message = String(writeError?.message || '') + ' ' + JSON.stringify(writeError?.data || {});
                        let offending = null;
                        for (const column of optionalColumns) {
                            if (column in payload && message.includes(column)) {
                                offending = column;
                                break;
                            }
                        }
                        if (!offending) throw writeError;
                        console.warn(`PocketBase settings schema lacks ${offending} column — retrying without it. Add the column to enable persistence.`);
                        const { [offending]: _drop, ...rest } = payload;
                        payload = rest;
                    }
                }

                return record.id;
            } catch (error) {
                console.error('Error saving settings in PocketBase:', error);
                throw error;
            }
        }

        throw new Error('PocketBase is required');
    }

    // Persist the per-user color map. Uses a partial update so it never touches
    // the prompt settings on the same record. Requires a `user_colors` field on
    // the settings collection; degrades gracefully (warns) if it's missing.
    async saveUserColors(userColors) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                const payload = { user_colors: JSON.stringify(userColors || {}) };
                const existing = await pb.collection('settings').getFullList();
                const record = existing.length > 0
                    ? await pb.collection('settings').update(existing[0].id, payload)
                    : await pb.collection('settings').create(payload);

                // PocketBase silently DROPS unknown fields instead of erroring, so a
                // missing `user_colors` column looks like a successful save. Detect it
                // by checking the field actually came back on the saved record.
                if (record.user_colors === undefined) {
                    console.warn('PocketBase settings collection has no `user_colors` field — colors are not persisted. Add a JSON field named user_colors to the settings collection.');
                    return { success: false, error: 'missing-field' };
                }
                return { success: true };
            } catch (error) {
                const message = String(error?.message || '') + ' ' + JSON.stringify(error?.data || {});
                if (message.includes('user_colors')) {
                    console.warn('PocketBase settings schema lacks user_colors column — colors not persisted. Add the column to enable it.');
                    return { success: false, error: 'missing-field' };
                }
                console.error('Error saving user colors in PocketBase:', error);
                throw error;
            }
        }

        throw new Error('PocketBase is required');
    }

    // === Bewerbungspool Methods ===

    // Get all applicants from Bewerbungspool
    async getApplicants() {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                const records = await pb.collection('applicants').getFullList({
                    sort: '-created',
                });
                
                return records.map(record => ({
                    leadID: record.lead_id || record.id,
                    status: record.status || 'Unbearbeitet',
                    name: record.name || '',
                    email: record.email || '',
                    phone: record.phone || '',
                    createdAt: record.created,
                    lastChange: record.updated,
                    rawFunnelData: record.funnel_data || [],
                    history: record.history || []
                }));
            } catch (error) {
                console.error('Error fetching applicants from PocketBase:', error);
                throw error;
            }
        } else {
            throw new Error('PocketBase is required');
        }
    }

    // Create a new applicant in Bewerbungspool
    async createApplicant(applicantData) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                const applicantRecord = {
                    lead_id: applicantData.leadID || '',
                    status: applicantData.status || 'Unbearbeitet',
                    name: applicantData.name || '',
                    email: applicantData.email || '',
                    phone: applicantData.phone || '',
                    funnel_data: applicantData.rawFunnelData || [],
                    history: applicantData.history || []
                };
                const record = await pb.collection('applicants').create(applicantRecord);
                return record.id;
            } catch (error) {
                console.error('Error creating applicant in PocketBase:', error);
                throw error;
            }
        }
        
        throw new Error('PocketBase is required');
    }

    // Update applicant status
    async updateApplicantStatus(leadID, newStatus) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                const updateData = {
                    status: newStatus,
                    updated: new Date().toISOString()
                };
                const records = await pb.collection('applicants').getFullList({
                    filter: `lead_id = "${leadID}"`
                });
                
                if (records.length > 0) {
                    await pb.collection('applicants').update(records[0].id, updateData);
                    return true;
                }
                return false;
            } catch (error) {
                console.error('Error updating applicant status in PocketBase:', error);
                throw error;
            }
        }
        
        throw new Error('PocketBase is required');
    }

    // Delete an applicant
    async deleteApplicant(leadID) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                requireAuth();
                const records = await pb.collection('applicants').getFullList({
                    filter: `lead_id = "${leadID}"`
                });
                
                if (records.length > 0) {
                    await pb.collection('applicants').delete(records[0].id);
                    return true;
                }
                return false;
            } catch (error) {
                console.error('Error deleting applicant from PocketBase:', error);
                throw error;
            }
        } else {
            throw new Error('PocketBase is required');
        }
    }

    // Import applicants from external source (e.g., JSON file)
    async importApplicants(applicantsData) {
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                const importResults = [];
                
                for (const applicant of applicantsData) {
                    try {
                        const id = await this.createApplicant(applicant);
                        importResults.push({ success: true, id, leadID: applicant.leadID });
                    } catch (error) {
                        importResults.push({ success: false, error: error.message, leadID: applicant.leadID });
                    }
                }
                
                return importResults;
            } catch (error) {
                console.error('Error importing applicants:', error);
                throw error;
            }
        }
        
        throw new Error('PocketBase is required');
    }

    // Default Questions Templates
    async getDefaultQuestionTemplates() {
        if (this.pb) {
            try {
                requireAuth();
                const records = await this.pb.collection('default_questions').getFullList({
                    sort: '-created'
                });
                return records;
            } catch (error) {
                console.error('Error fetching default question templates:', error);
                throw error;
            }
        }
        throw new Error('PocketBase is required');
    }

    async saveDefaultQuestionTemplate(templateData) {
        if (this.pb) {
            try {
                requireAuth();
                const record = await this.pb.collection('default_questions').create(templateData);
                return record.id;
            } catch (error) {
                console.error('Error saving default question template:', error);
                throw error;
            }
        }
        throw new Error('PocketBase is required');
    }

    async updateDefaultQuestionTemplate(templateId, templateData) {
        if (this.pb) {
            try {
                requireAuth();
                const record = await this.pb.collection('default_questions').update(templateId, templateData);
                return record.id;
            } catch (error) {
                console.error('Error updating default question template:', error);
                throw error;
            }
        }
        throw new Error('PocketBase is required');
    }

    async deleteDefaultQuestionTemplate(templateId) {
        if (this.pb) {
            try {
                requireAuth();
                await this.pb.collection('default_questions').delete(templateId);
                return true;
            } catch (error) {
                console.error('Error deleting default question template:', error);
                throw error;
            }
        }
        throw new Error('PocketBase is required');
    }

    // Basis Questions Templates
    async getBasicQuestionTemplates() {
        if (this.pb) {
            try {
                requireAuth();
                const records = await this.pb.collection('basic_questions').getFullList({
                    sort: '-created'
                });
                return records;
            } catch (error) {
                console.error('Error fetching basic question templates:', error);
                throw error;
            }
        }
        throw new Error('PocketBase is required');
    }

    async saveBasicQuestionTemplate(templateData) {
        if (this.pb) {
            try {
                requireAuth();
                const record = await this.pb.collection('basic_questions').create(templateData);
                return record.id;
            } catch (error) {
                console.error('Error saving basic question template:', error);
                throw error;
            }
        }
        throw new Error('PocketBase is required');
    }

    async updateBasicQuestionTemplate(templateId, templateData) {
        if (this.pb) {
            try {
                requireAuth();
                const record = await this.pb.collection('basic_questions').update(templateId, templateData);
                return record.id;
            } catch (error) {
                console.error('Error updating basic question template:', error);
                throw error;
            }
        }
        throw new Error('PocketBase is required');
    }

    async deleteBasicQuestionTemplate(templateId) {
        if (this.pb) {
            try {
                requireAuth();
                await this.pb.collection('basic_questions').delete(templateId);
                return true;
            } catch (error) {
                console.error('Error deleting basic question template:', error);
                throw error;
            }
        }
        throw new Error('PocketBase is required');
    }

    async getFachQuestionTemplates() {
        if (this.pb) {
            try {
                requireAuth();
                const records = await this.pb.collection('fach_questions').getFullList({
                    sort: '-created'
                });
                return records;
            } catch (error) {
                console.error('Error fetching fach question templates:', error);
                throw error;
            }
        }
        throw new Error('PocketBase is required');
    }

    async saveFachQuestionTemplate(templateData) {
        if (this.pb) {
            try {
                requireAuth();
                const record = await this.pb.collection('fach_questions').create(templateData);
                return record.id;
            } catch (error) {
                console.error('Error saving fach question template:', error);
                throw error;
            }
        }
        throw new Error('PocketBase is required');
    }

    async updateFachQuestionTemplate(templateId, templateData) {
        if (this.pb) {
            try {
                requireAuth();
                const record = await this.pb.collection('fach_questions').update(templateId, templateData);
                return record.id;
            } catch (error) {
                console.error('Error updating fach question template:', error);
                throw error;
            }
        }
        throw new Error('PocketBase is required');
    }

    async deleteFachQuestionTemplate(templateId) {
        if (this.pb) {
            try {
                requireAuth();
                await this.pb.collection('fach_questions').delete(templateId);
                return true;
            } catch (error) {
                console.error('Error deleting fach question template:', error);
                throw error;
            }
        }
        throw new Error('PocketBase is required');
    }

    // === Landing Page Templates ===
    async getLandingPageTemplates() {
        if (this.pb) {
            try {
                requireAuth();
                const records = await this.pb.collection('landing_page_templates').getFullList({ sort: '-created' });
                return records.map(r => ({ id: r.id, name: r.name, landing_page_json: r.landing_page_json, created: r.created, updated: r.updated }));
            } catch (error) {
                console.error('Error fetching landing page templates:', error);
                throw error;
            }
        }
        throw new Error('PocketBase is required');
    }

    async createLandingPageTemplate(name, landingPageJson) {
        if (this.pb) {
            try {
                requireAuth();
                const data = { name, landing_page_json: landingPageJson };
                const record = await this.pb.collection('landing_page_templates').create(data);
                return { id: record.id, name: record.name, landing_page_json: record.landing_page_json, created: record.created, updated: record.updated };
            } catch (error) {
                console.error('Error creating landing page template:', error);
                throw error;
            }
        }
        throw new Error('PocketBase is required');
    }

    async deleteLandingPageTemplate(id) {
        if (this.pb) {
            try {
                requireAuth();
                await this.pb.collection('landing_page_templates').delete(id);
                return true;
            } catch (error) {
                console.error('Error deleting landing page template:', error);
                throw error;
            }
        }
        throw new Error('PocketBase is required');
    }
}

// Globale Datenbankinstanz
export const dbService = new DatabaseService();
